import type { CsfDataViewDto, FiscalRegimesDto } from '@subscriptions/application/dtos';

/** Form fields the CSF can fill; email is never in the document, so it is left out */
export type CsfPrefillValues = {
  businessName: string;
  firstName: string;
  firstLastName: string;
  secondLastName: string;
  postalCode: string;
  rfc: string;
  taxRegimeSatCode: string;
};

/** An RFC of 12 characters belongs to a persona moral; 13 to a persona física */
const COMPANY_RFC_LENGTH = 12;

/** Words that carry no meaning for the match: the two vocabularies punctuate and join differently */
const CONNECTORS = new Set(['REGIMEN', 'DE', 'DEL', 'LA', 'LAS', 'LOS', 'EL', 'Y', 'E', 'O', 'U', 'A', 'AL', 'EN']);

const toWords = (value: string): Set<string> =>
  new Set(
    value
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, ' ')
      .trim()
      .split(' ')
      .filter((word) => word && !CONNECTORS.has(word)),
  );

const countShared = (words: Set<string>, other: Set<string>): number =>
  [...words].filter((word) => other.has(word)).length;

/**
 * Resolves the SAT code for the regime description printed in the CSF.
 *
 * The document and the catalog word the same regime differently — "Sueldos y Salarios e
 * Ingresos…" against "Sueldos, Salarios e Ingresos…" — so they are compared as sets of
 * meaningful words instead of as strings. An equal set wins; otherwise the most specific
 * candidate whose words are fully contained on either side is taken, and anything looser
 * leaves the select empty rather than guessing a regime the invoice would be stamped with.
 *
 * @param description - Regime as printed in the CSF
 * @param regimes - SAT catalog for the person type
 * @returns The matching SAT code, or an empty string when none matches
 */
const resolveTaxRegimeSatCode = (description: string, regimes: { satCode: string; description: string }[]): string => {
  const target = toWords(description);
  if (!target.size) return '';

  const candidates = regimes.map((regime) => ({ satCode: regime.satCode, words: toWords(regime.description) }));

  const exact = candidates.find(
    (candidate) => candidate.words.size === target.size && countShared(candidate.words, target) === target.size,
  );
  if (exact) return exact.satCode;

  // A subset is only acceptable with real evidence: a single word ("INGRESOS")
  // does not distinguish between 605, 608, 611, 614, 615 and 625.
  const contained = candidates
    .map((candidate) => ({ ...candidate, shared: countShared(candidate.words, target) }))
    .filter(({ words, shared }) => shared >= 2 && (shared === words.size || shared === target.size))
    // Tiebreak by specificity (fewer extra words), not by catalog position.
    .sort((a, b) => b.shared - a.shared || a.words.size - b.words.size);

  // Without a clear winner, an empty select is better than a guessed regime on the CFDI.
  if (contained.length > 1 && contained[0].shared === contained[1].shared) return '';

  return contained[0]?.satCode ?? '';
};

/**
 * Strips the two last names off the full name the CSF reports for a persona física.
 *
 * Removes tokens from the end one by one, comparing case-insensitively.
 * If a last name token doesn't match the last remaining token, the pop is skipped
 * and iteration continues — leaving the unmatched token in place for the user to correct.
 *
 * @param businessName - Full name as printed in the CSF
 * @param firstLastName - Paternal last name
 * @param secondLastName - Maternal last name
 * @returns The given names alone
 */
const resolveFirstName = (businessName: string, firstLastName: string, secondLastName: string): string => {
  // Consumed from the end: maternal first, then paternal.
  const lastNameTokens = `${secondLastName} ${firstLastName}`.trim().split(/\s+/).filter(Boolean);
  if (!lastNameTokens.length) return businessName.trim();

  const tokens = businessName.trim().split(/\s+/);
  lastNameTokens.forEach((lastName) => {
    if (tokens[tokens.length - 1]?.toUpperCase() === lastName.toUpperCase()) tokens.pop();
  });

  return tokens.join(' ');
};

/**
 * Maps the extracted CSF data onto the invoice data form fields.
 *
 * The person type is derived from the RFC length, the same rule the form itself applies, so a
 * moral fills `businessName` and a física the three name fields.
 *
 * @param csfData - Fiscal data extracted from the document
 * @param fiscalRegimes - SAT catalog used to resolve the regime code
 * @returns Values ready to reset the form with
 */
export const mapCsfDataToFormValues = (csfData: CsfDataViewDto, fiscalRegimes?: FiscalRegimesDto): CsfPrefillValues => {
  const rfc = csfData.rfc.trim().toUpperCase();
  const isCompany = rfc.length === COMPANY_RFC_LENGTH;
  const regimes = (isCompany ? fiscalRegimes?.taxRegimeCompany : fiscalRegimes?.taxRegimePersons) ?? [];
  const businessName = csfData.businessName.trim().toUpperCase();
  const firstLastName = csfData.firstLastName.trim().toUpperCase();
  const secondLastName = csfData.secondLastName.trim().toUpperCase();

  return {
    businessName: isCompany ? businessName : '',
    firstName: isCompany ? '' : resolveFirstName(businessName, firstLastName, secondLastName),
    firstLastName: isCompany ? '' : firstLastName,
    secondLastName: isCompany ? '' : secondLastName,
    postalCode: csfData.postalCode.trim(),
    rfc,
    taxRegimeSatCode: resolveTaxRegimeSatCode(csfData.taxRegimeDescription, regimes),
  };
};
